<template>
  <div>
    <div class="card" :class="eventTypeClass">
      <div class="head">
        <template v-if="category">
          <img :src="categoryIcon" alt="" />
          <span>{{ category }}</span>
        </template>
      </div>

      <div class="cardContent">
        <div class="content">
          <template v-if="rectangle">
            <div class="title">
              <h3>{{ title }}</h3>
            </div>

            <div
              class="eventTypeContainer"
              v-if="eventTypes && eventTypes.length"
            >
              <div class="eventType">
                <span
                  v-for="type in eventTypes"
                  :key="type"
                  :class="getEventLabelClass(type)"
                >
                  {{ type }}
                </span>
              </div>
              <div class="imgWrap">
                <img :src="thumbnail" alt="" />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="title">
              <h3>{{ title }}</h3>
              <div>
                <img :src="thumbnail" alt="" />
              </div>
            </div>

            <div class="eventType" v-if="eventTypes && eventTypes.length">
              <span
                v-for="type in eventTypes"
                :key="type"
                :class="getEventLabelClass(type)"
              >
                {{ type }}
              </span>
            </div>
          </template>

          <div class="split" v-if="date">
            <dt>日時</dt>
            <dd>{{ date }}</dd>
          </div>

          <div class="split" v-if="place">
            <dt>会場</dt>
            <dd>{{ place }}</dd>
          </div>

          <div class="tags" v-if="tags && tags.length">
            <Tag :text="tag" v-for="tag in tags" :key="tag" check icon="tag" />
          </div>

          <div class="desc" v-if="description">
            {{ description }}
          </div>
        </div>

        <div>
          <div v-if="isFinished" class="finishedText">
            このイベントは終了しました
          </div>
          <div v-if="!isFinished">
            <Button
              class="button"
              :href="link"
              :label="buttonLabel"
              :handleClick="() => $router.push(link)"
              secondary
            />
            <Button
              v-if="isCancelable"
              class="button"
              label="キャンセル"
              :handleClick="clickCancel"
              thingray
            />
          </div>
          <Button
            v-if="isArchived"
            class="button"
            :href="link"
            label="アーカイブを見る"
            :handleClick="() => $router.push(link)"
            primary
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'
import Tag from '@/components/elements/Tag.vue'
import Button from '@/components/elements/Button.vue'
export default defineComponent({
  name: 'EventCard',
  components: {
    Tag,
    Button
  },
  props: {
    title: {
      type: String,
      required: true
    },
    thumbnail: {
      type: String,
      default: require('@/assets/images/common/noimg.jpg') as string
    },
    eventTypes: Array as PropType<string[]>,
    date: {
      type: String,
      default: ''
    },
    place: {
      type: String,
      default: ''
    },
    tags: Array,
    description: {
      type: String,
      default: ''
    },
    link: String,
    category: {
      type: String,
      default: ''
    },
    buttonLabel: {
      type: String,
      default: '詳細・予約'
    },
    rectangle: Boolean,
    isFinished: {
      type: Boolean,
      default: false
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    isCancelable: {
      type: Boolean,
      default: false
    },
    clickCancel: Function
  },
  setup(props) {
    const isOnline = computed(() => props.eventTypes?.includes('オンライン'))
    const isOffline = computed(() => props.eventTypes?.includes('オフライン'))
    const isCareer = computed(() =>
      props.eventTypes?.includes('キャリアガイダンス')
    )

    const categoryIcon = computed(() => {
      if (isCareer.value) return require('@/assets/images/icon/book-white.svg')
      if (isOnline.value) return require('@/assets/images/icon/pc-white.svg')
      if (isOffline.value)
        return require('@/assets/images/icon/hospital-02-white.svg')
    })
    const eventTypeClass = computed(() => {
      if (isCareer.value) return 'isCareer'
      if (isOnline.value) return 'isOnline'
      if (isOffline.value) return 'isOffline'
    })
    const getEventLabelClass = (eventType: string): string | undefined => {
      if (eventType === 'キャリアガイダンス') return 'isCareer'
      if (eventType === 'オンライン') return 'isOnline'
      if (eventType === 'オフライン') return 'isOffline'
    }

    return {
      categoryIcon,
      eventTypeClass,
      getEventLabelClass
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.card {
  width: 100%;
  height: 100%;
  border: solid 1px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border: 0.5px solid var.$color-border-gray;
  background-color: var.$color-white;

  &.isOnline {
    .head {
      background-color: #36c0f2;
    }
  }
  &.isOffline {
    .head {
      background-color: var.$color-primary;
    }
  }
  &.isCareer {
    .head {
      background-color: var.$color-main;
    }
  }
}

.cardContent {
  height: calc(100% - 32px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 20px 24px;
}

.content {
  font-size: 13px;
  display: flex;
  flex-direction: column;
  > * {
    margin-bottom: 8px;
  }
}

.head {
  background-color: var.$color-main;
  color: var.$color-white;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 22px;
  padding: 5px 0;
  font-weight: bold;
  height: 32px;
  letter-spacing: 0;
  img {
    height: 13px;
    margin-right: 4px;
  }
}

.title {
  h3 {
    font-size: 18px;
    font-weight: 500;
    line-height: 2.2rem;
  }
  div {
    min-width: 70px;
    margin-left: 24px;
  }
  img {
    width: 70px;
    object-fit: contain;
  }
  display: flex;
  justify-content: space-between;
}

.eventType {
  display: flex;
  flex-wrap: wrap;
  span {
    background-color: var.$color-accent-back;
    padding: 0 5px;
    font-size: 1rem;
    line-height: 18px;
    font-weight: 500;
    margin-right: 8px;
    margin-bottom: 4px;
    &.isOnline {
      background-color: #36c0f2;
      color: #fff;
    }
    &.isOffline {
      background-color: var.$color-primary;
      color: #fff;
    }
    &.isCareer {
      background-color: var.$color-main;
      color: #fff;
    }
  }
}
.eventTypeContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .eventType {
    flex: 1;
  }
  .imgWrap {
    flex-basis: 70px;
  }
  img {
    width: 70px;
    object-fit: contain;
  }
}

.split {
  display: flex;
  dt {
    min-width: 30px;
    font-weight: 500;
  }
  dd {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin-left: 24px;
  }
}

.tags {
  display: flex;
  flex-wrap: wrap;
  > * {
    margin-right: 4px;
    margin-bottom: 4px;
  }
}

.desc {
  letter-spacing: 0.02px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
}

.finishedText {
  height: 40px;
  margin-top: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  border-radius: 4px;
  background: #f5f5f5;
}

::v-deep .button {
  min-height: 40px;
  margin-top: 12px;
}

::v-deep .button + .button,
::v-deep .finishedText + .button {
  margin-top: 16px;
}
</style>
